/**
 * Which workspace a rendered surface (an editor tab, an app preview) lives in
 * — the target a client-executed agent tool is bound to (#352).
 *
 * The workspace's editor stack carries `data-workspace-id`; a surface asks the
 * DOM (`closest`) rather than a prop, so EditorMount and every editor below it
 * need no threading, and a query can be scoped to one workspace's subtree
 * (`querySelectorAll` under the marked element). Every open workspace stays
 * mounted (the desktop shell's keep-alive decks), so "the focused surface" is
 * ambiguous across them — this is what disambiguates.
 */
export const WORKSPACE_SCOPE_ATTR = 'data-workspace-id'

/** Set on a marked stack once its tabs are restored ("1"); until then a
 *  surface that belongs to it may simply not be mounted yet. A stack with
 *  nothing to restore (chat-home's single editor) is marked loaded at once. */
export const WORKSPACE_LOADED_ATTR = 'data-workspace-loaded'

/** Set ("1") on a stack that holds exactly one editor (chat-home's side
 *  editor): "loaded" there means its one file viewer has rendered, which the
 *  stack itself cannot see — the editor shows a spinner until its file
 *  answers. A single stack with no viewer in it yet is still mounting. */
export const WORKSPACE_SINGLE_ATTR = 'data-workspace-single'

/** A deck's own route (`/~/workspace/<folder>` or `/t/<tenant>/workspace/
 *  <folder>`), on its stack — what a deep link to a file IN that workspace is
 *  built from. The deck knows its tenant; the page URL does not always
 *  (settings, bin, an app). */
export const WORKSPACE_HREF_ATTR = 'data-workspace-href'

/** A link that opens `fileId` in `workspaceId` — its deck's route when one of
 *  its stacks carries it, else the current page (chat-home, mobile). */
export function workspaceFileLink(workspaceId: string | null, fileId: string): string {
  const href = workspaceId
    ? workspaceScopeRoots(workspaceId).map(r => r.getAttribute(WORKSPACE_HREF_ATTR)).find(Boolean)
    : null
  return `${window.location.origin}${href || window.location.pathname}?file=${encodeURIComponent(fileId)}`
}

/** CSS.escape with a manual fallback (jsdom / very old WebViews lack it). */
export function cssEscape(value: string): string {
  const g = (globalThis as unknown as { CSS?: { escape?: (v: string) => string } }).CSS
  if (g?.escape) return g.escape(value)
  return value.replace(/["\\\]#.:>~+*^$|=[()\s]/g, '\\$&')
}

/** Is `el` the SHOWN one within `root` — nothing between them hidden?
 *  Workspace tab groups hide inactive tabs with an inline `display: none`;
 *  that is the one rule both the database binding and the preview lookup
 *  read. (Whether `root` itself is on screen is a different question.) */
export function isShownWithin(el: Element, root: Element): boolean {
  for (let n = el.parentElement; n && n !== root; n = n.parentElement) {
    if ((n as HTMLElement).style?.display === 'none') return false
  }
  return true
}

/** Is a stack rendered on screen right now (not inside a hidden deck)? Best
 *  effort: no layout (jsdom) reads as not-on-screen, so callers use it only to
 *  PREFER one stack over another, never to exclude. */
export function isRenderedOnScreen(el: Element): boolean {
  try { return el.getClientRects().length > 0 } catch { return false }
}

/** The workspace an element renders in, or `null` outside any workspace
 *  (a single-file view, the drive). */
export function workspaceIdOf(el: Element | null | undefined): string | null {
  const host = el?.closest(`[${WORKSPACE_SCOPE_ATTR}]`)
  return host?.getAttribute(WORKSPACE_SCOPE_ATTR) || null
}

/** Every marked stack of one workspace — it can render in more than one
 *  place at once (its own deck, and chat-home's side editor for the same
 *  project). Empty when none is mounted. */
export function workspaceScopeRoots(workspaceId: string): Element[] {
  if (typeof document === 'undefined') return []
  const roots = Array.from(document.querySelectorAll(`[${WORKSPACE_SCOPE_ATTR}="${cssEscape(workspaceId)}"]`))
  if (roots.length < 2) return roots
  // The stack on screen first — the copy a person is looking at (measured once each).
  return roots.map(r => [r, isRenderedOnScreen(r)] as const).sort((a, b) => Number(b[1]) - Number(a[1])).map(([r]) => r)
}

/** Is that stack's tab set restored (see {@link WORKSPACE_LOADED_ATTR}) —
 *  and, for a single-editor stack, has its viewer rendered? */
export function workspaceScopeLoaded(root: Element): boolean {
  if (root.getAttribute(WORKSPACE_LOADED_ATTR) !== '1') return false
  // Any FINAL state counts: a viewer, or one of the screens that replace it
  // when the file cannot be shown (unavailable, unable to open, deleted…) —
  // else a bound call waited 5 min on an editor that will never mount.
  if (root.getAttribute(WORKSPACE_SINGLE_ATTR) === '1') {
    return !!root.querySelector('[data-file-id], [data-testid="file-unavailable"], [data-testid="singlefile-failure-title"]')
  }
  return true
}
